import { axiosInstance } from "@kubb/plugin-client/clients/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import React from "react";
import { useLocation, useNavigate } from "react-router";
import { AUTH_CONSTANTS } from "../../../shared/constants/auth.constants";
import type { AuthenticatedUser } from "../../../shared/types/auth.types";
import type { LoginMutationRequest } from "../../../shared/types/authApi.types";

interface AuthContextType {
	user: AuthenticatedUser | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	login: (credentials: LoginMutationRequest) => Promise<void>;
	logout: () => Promise<void>;
	checkSession: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

// Authentication API functions
const authenticationApi = {
	login: async (credentials: LoginMutationRequest) => {
		try {
			const response = await axiosInstance.post(
				"/api2/auth/login",
				credentials,
				{
					withCredentials: true,
				},
			);
			return response.data;
		} catch (error: any) {
			throw new Error(error.response?.data?.message || "Login failed");
		}
	},

	logout: async () => {
		try {
			console.log("Logging out...");
			const response = await axiosInstance.post(
				"/api2/auth/logout",
				{},
				{
					withCredentials: true,
				},
			);
			return response.data;
		} catch (_error: any) {
			throw new Error("Logout failed");
		}
	},
};

// Session API functions
const sessionApi = {
	checkSession: async () => {
		try {
			const response = await axiosInstance.get("/api2/auth/session", {
				withCredentials: true,
			});
			return response.data;
		} catch (error: any) {
			// Don't throw error for unauthorized - return unauthenticated state instead
			if (error.response?.status === 401) {
				return { isAuthenticated: false, user: null };
			}
			throw new Error(
				`Session check failed: ${error.response?.status} ${error.response?.statusText}`,
			);
		}
	},
};

export const useAuth = () => {
	const context = React.useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const [user, setUser] = React.useState(null);
	const [isAuthenticated, setIsAuthenticated] = React.useState(false);
	const [hasInitialized, setHasInitialized] = React.useState(false);
	const [isInitialLoad, setIsInitialLoad] = React.useState(true);
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const location = useLocation();

	// Prevent immediate redirects on initial page load
	React.useEffect(() => {
		const timer = setTimeout(() => {
			setIsInitialLoad(false);
		}, 500); // Give 500ms for auth check to complete

		return () => clearTimeout(timer);
	}, []);

	const {
		data: sessionData,
		isLoading: queryIsLoading,
		error,
		isFetching,
	} = useQuery({
		queryKey: ["auth", "session"],
		queryFn: sessionApi.checkSession,
		retry: 1, // Retry once if the first request fails
		refetchOnWindowFocus: true, // Check auth status when window gains focus
		refetchOnReconnect: true, // Check auth status when reconnecting
		staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
		// Don't fail the entire auth if session check fails
		throwOnError: false,
		// CRITICAL: Make sure query is enabled immediately
		enabled: true,
		// Prevent automatic background refetching that could cause loading flicker
		refetchOnMount: true,
	});

	// Calculate the actual loading state - prevent redirects during initial load
	const isLoading =
		queryIsLoading || isFetching || !hasInitialized || isInitialLoad;

	// Handle session check success/error with useEffect
	React.useEffect(() => {
		if (queryIsLoading || isFetching) {
			return;
		}

		// Only trust the response if we got a valid response (not an error)
		const typedSessionData = sessionData as any;
		if (
			typedSessionData &&
			typeof typedSessionData.isAuthenticated === "boolean"
		) {
			if (typedSessionData.isAuthenticated && typedSessionData.user) {
				setUser(typedSessionData.user);
				setIsAuthenticated(true);
			} else {
				setUser(null);
				setIsAuthenticated(false);
			}
			setHasInitialized(true);
		} else if (error) {
			setUser(null);
			setIsAuthenticated(false);
			setHasInitialized(true);
		}
	}, [sessionData, queryIsLoading, isFetching, error]);

	const loginMutation = useMutation({
		mutationFn: authenticationApi.login,
		onSuccess: (data: any) => {
			// Update auth state immediately with login response
			if (data.success && data.user) {
				setUser(data.user);
				setIsAuthenticated(true);
				setHasInitialized(true);
			}

			queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
			message.success(AUTH_CONSTANTS.SUCCESS_LOGIN_MSG);

			// Redirect back to intended destination or home page
			const intendedDestination =
				location.state?.from?.pathname || AUTH_CONSTANTS.HOME_ROUTE;

			setTimeout(() => {
				navigate(intendedDestination, { replace: true });
			}, 100);
		},
		onError: (error: any) => {
			if (error.message.includes("mismatch")) {
				message.warning(AUTH_CONSTANTS.ERROR_ADMIN_LOGIN_MISMATCH_MSG);
			} else {
				message.error(AUTH_CONSTANTS.ERROR_SYSTEM_MSG);
			}
		},
	});

	const logoutMutation = useMutation({
		mutationFn: authenticationApi.logout,
		onSuccess: () => {
			setUser(null);
			setIsAuthenticated(false);
			setHasInitialized(true);
			queryClient.clear();
			navigate(AUTH_CONSTANTS.LOGIN_ROUTE);
		},
		onError: () => {
			message.error(AUTH_CONSTANTS.ERROR_SYSTEM_MSG);
		},
	});

	const login = async (credentials: LoginMutationRequest) => {
		return loginMutation.mutateAsync(credentials);
	};

	const logout = async () => {
		return logoutMutation.mutateAsync();
	};

	const checkSession = async () => {
		return queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
	};

	const contextValue = {
		user,
		isAuthenticated,
		// Show loading until auth query completes AND we've initialized the state
		isLoading:
			isLoading ||
			!hasInitialized ||
			loginMutation.isPending ||
			logoutMutation.isPending,
		login,
		logout,
		checkSession,
	};

	return React.createElement(
		AuthContext.Provider,
		{ value: contextValue },
		children,
	);
};
